import React from 'react';

export interface IconProps {
  className?: string;
  size?: number;
  color?: string;
}

export interface ActiveIconProps extends IconProps {
  isActive?: boolean;
}

const S = 1.5;
const CAP = 'round' as const;
const JOIN = 'round' as const;
const BRAND = '#e53935';
const BRAND_DIM = '#c62828';

const IconBase: React.FC<
  IconProps & { children: React.ReactNode; viewBox?: string }
> = ({ className = '', size = 24, children, viewBox = '0 0 24 24' }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox={viewBox}
    fill="none"
    aria-hidden="true"
  >
    {children}
  </svg>
);

export const InjectedMarkIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <path
      d="M12 3L19 7.5V16.5L12 21L5 16.5V7.5L12 3Z"
      stroke={color}
      strokeWidth="1.25"
      strokeLinejoin={JOIN}
      strokeOpacity="0.85"
    />
    <path d="M12 7v10M9 10h6M9 14h6" stroke={BRAND} strokeWidth="1.25" strokeLinecap={CAP} />
    <circle cx="12" cy="12" r="1.75" fill={BRAND}>
      <animate attributeName="opacity" values="0.65;1;0.65" dur="2.4s" repeatCount="indefinite" />
    </circle>
    <path d="M17 5.5l2 2M19 5.5L17 7.5" stroke={BRAND_DIM} strokeWidth="1.1" strokeLinecap={CAP} />
  </IconBase>
);

export const GlitchCloudIcon = InjectedMarkIcon;

export { InjectedSyringeCloudLogo } from './InjectedSyringeCloudLogo';
export type { InjectedSyringeCloudLogoProps, InjectedSyringeCloudLogoVariant } from './InjectedSyringeCloudLogo';

export const DashboardIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <rect x="4" y="4" width="7" height="7" rx="1" stroke={color} strokeWidth={S} strokeOpacity="0.85" />
    <rect x="13" y="4" width="7" height="7" rx="1" stroke={color} strokeWidth={S} strokeOpacity="0.5" />
    <rect x="4" y="13" width="7" height="7" rx="1" stroke={color} strokeWidth={S} strokeOpacity="0.5" />
    <rect x="13" y="13" width="7" height="7" rx="1" fill="rgba(229, 57, 53,0.12)" stroke="#e53935" strokeWidth={S} />
    <circle cx="12" cy="12" r="1" fill="#e53935" />
  </IconBase>
);

export const WelcomeDashboardIcon = DashboardIcon;

export const DomainIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <circle cx="12" cy="12" r="7" stroke={color} strokeWidth={S} strokeOpacity="0.75" />
    <ellipse cx="12" cy="12" rx="3" ry="7" stroke={color} strokeWidth="1" strokeOpacity="0.4" />
    <path d="M5 10.5h14M5 13.5h14" stroke={color} strokeWidth="1" strokeOpacity="0.35" strokeLinecap={CAP} />
    <circle cx="12" cy="12" r="1" fill="#e53935" />
  </IconBase>
);

export const StatsIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <path d="M5 18V14M9 18V10M13 18V12M17 18V8" stroke={color} strokeWidth={S} strokeLinecap={CAP} strokeOpacity="0.85" />
    <path d="M4 18h16" stroke={color} strokeWidth="1" strokeOpacity="0.35" strokeLinecap={CAP} />
    <circle cx="17" cy="8" r="1.25" fill="#e53935" />
  </IconBase>
);

export const ResultsIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <circle cx="12" cy="12" r="6.5" stroke={color} strokeWidth={S} strokeOpacity="0.65" />
    <path d="M12 6.5v13M6.5 12h13" stroke={color} strokeWidth="1" strokeOpacity="0.3" strokeLinecap={CAP} />
    <circle cx="12" cy="12" r="2" fill="rgba(229, 57, 53,0.15)" stroke="#e53935" strokeWidth={S} />
    <path d="M10.5 12l1.2 1.2 2.3-2.8" stroke="#4ade80" strokeWidth="1.25" strokeLinecap={CAP} strokeLinejoin={JOIN} />
  </IconBase>
);

export const GrabberIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <path
      d="M8 5h8l-1.5 4H9.5L8 5zM9 9h6l-1 8H10L9 9z"
      stroke={color}
      strokeWidth={S}
      strokeLinejoin={JOIN}
      strokeOpacity="0.85"
    />
    <path d="M7 18.5h10" stroke="#e53935" strokeWidth="1.25" strokeLinecap={CAP} />
  </IconBase>
);

export const MailerIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <rect x="5" y="6.5" width="14" height="10" rx="1" stroke={color} strokeWidth={S} strokeOpacity="0.8" />
    <path d="M5 7.5l7 5 7-5" stroke={color} strokeWidth={S} strokeLinecap={CAP} strokeLinejoin={JOIN} strokeOpacity="0.65" />
    <path d="M8 14.5h8" stroke={color} strokeWidth="1" strokeLinecap={CAP} strokeOpacity="0.35" />
    <circle cx="16.5" cy="8.5" r="1" fill="#e53935" />
  </IconBase>
);

export const DebouncerIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <path
      d="M6 8.5h12M6 12h8M6 15.5h10"
      stroke={color}
      strokeWidth={S}
      strokeLinecap={CAP}
      strokeOpacity="0.75"
    />
    <circle cx="17" cy="12" r="2.5" stroke="#e53935" strokeWidth="1.25" />
    <path d="M16 12l1.2 1.2 2-2.5" stroke="#4ade80" strokeWidth="1.1" strokeLinecap={CAP} strokeLinejoin={JOIN} />
  </IconBase>
);

export const StripeIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <rect
      x="5"
      y="8"
      width="14"
      height="9"
      rx="1.5"
      stroke={color}
      strokeWidth={S}
      strokeOpacity="0.75"
    />
    <path
      d="M5 11.5h14"
      stroke={color}
      strokeWidth="1"
      strokeOpacity="0.35"
      strokeLinecap={CAP}
    />
    <path
      d="M8 14.5h3M13 14.5h3"
      stroke="#e53935"
      strokeWidth="1.25"
      strokeLinecap={CAP}
    />
    <circle cx="17" cy="7" r="1.25" fill="#635bff" />
  </IconBase>
);

export const ConfigIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <circle cx="12" cy="12" r="2.5" stroke={color} strokeWidth={S} strokeOpacity="0.85" />
    <path
      d="M12 4.5v2.5M12 17v2.5M4.5 12h2.5M17 12h2.5M6.8 6.8l1.8 1.8M15.4 15.4l1.8 1.8M6.8 17.2l1.8-1.8M15.4 8.6l1.8-1.8"
      stroke={color}
      strokeWidth="1.25"
      strokeLinecap={CAP}
      strokeOpacity="0.6"
    />
    <circle cx="12" cy="12" r="0.75" fill="#e53935" />
  </IconBase>
);

export const ScannerIcon: React.FC<ActiveIconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
  isActive = false,
}) => (
  <IconBase className={className} size={size}>
    <path
      d="M4 8V4h4M16 4h4v4M20 16v4h-4M8 20H4v-4"
      stroke={color}
      strokeWidth={S}
      strokeLinecap={CAP}
      strokeLinejoin={JOIN}
      strokeOpacity="0.85"
    />
    {isActive ? (
      <line x1="4" y1="12" x2="20" y2="12" stroke="#e53935" strokeWidth="1.25" strokeLinecap={CAP}>
        <animate attributeName="y1" values="8;16;8" dur="2s" repeatCount="indefinite" />
        <animate attributeName="y2" values="8;16;8" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.45;1;0.45" dur="2s" repeatCount="indefinite" />
      </line>
    ) : (
      <line x1="4" y1="12" x2="20" y2="12" stroke={color} strokeWidth="1" strokeOpacity="0.25" strokeLinecap={CAP} />
    )}
    <circle cx="12" cy="12" r="1" fill={isActive ? '#e53935' : color} fillOpacity={isActive ? 1 : 0.35} />
  </IconBase>
);

export const ChartIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <path
      d="M4 17l4-5 3.5 3 3-4.5L20 17"
      stroke="#e53935"
      strokeWidth={S}
      strokeLinecap={CAP}
      strokeLinejoin={JOIN}
    />
    <path d="M4 17h16" stroke={color} strokeWidth="1" strokeOpacity="0.35" strokeLinecap={CAP} />
    <circle cx="20" cy="10.5" r="1.25" fill="#e53935" />
  </IconBase>
);

export const PaymentIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <rect x="4" y="6" width="16" height="12" rx="1.5" stroke={color} strokeWidth={S} strokeOpacity="0.75" />
    <path d="M4 10h16" stroke={color} strokeWidth="1" strokeOpacity="0.35" />
    <path d="M7 14.5h4" stroke="#e53935" strokeWidth="1.25" strokeLinecap={CAP} />
    <circle cx="16" cy="14.5" r="1.25" stroke="#e53935" strokeWidth="1" />
  </IconBase>
);

export const CloudScannerIcon = ScannerIcon;

export const LicenseIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <path
      d="M12 3.5l6.5 2.8v4.8c0 3.7-2.8 6.9-6.5 7.9-3.7-1-6.5-4.2-6.5-7.9V6.3L12 3.5z"
      stroke={color}
      strokeWidth={S}
      strokeLinejoin={JOIN}
      strokeOpacity="0.8"
    />
    <path d="M9.5 12.5l1.8 1.8L15 10.5" stroke="#e53935" strokeWidth="1.25" strokeLinecap={CAP} strokeLinejoin={JOIN} />
  </IconBase>
);

export const UserIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <circle cx="12" cy="8.5" r="3" stroke={color} strokeWidth={S} strokeOpacity="0.75" />
    <path
      d="M6 19.5c0-3 2.7-5 6-5s6 2 6 5"
      stroke={color}
      strokeWidth={S}
      strokeLinecap={CAP}
      strokeOpacity="0.75"
    />
    <circle cx="17" cy="7" r="1.25" fill="#4ade80" />
  </IconBase>
);

export const ClockIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <circle cx="12" cy="12" r="7" stroke={color} strokeWidth={S} strokeOpacity="0.7" />
    <path d="M12 8v4.5l2.8 1.7" stroke="#e53935" strokeWidth={S} strokeLinecap={CAP} strokeLinejoin={JOIN} />
  </IconBase>
);

export const StatusIcon: React.FC<ActiveIconProps> = ({
  className = '',
  size = 24,
  isActive = false,
}) => (
  <IconBase className={className} size={size}>
    <circle
      cx="12"
      cy="12"
      r="7"
      fill={isActive ? 'rgba(74, 222, 128, 0.12)' : 'rgba(248, 113, 113, 0.1)'}
      stroke={isActive ? '#4ade80' : '#fca5a5'}
      strokeWidth="1.25"
    />
    {isActive ? (
      <path d="M8.5 12.5l2 2 5-5" stroke="#4ade80" strokeWidth="1.5" strokeLinecap={CAP} strokeLinejoin={JOIN} />
    ) : (
      <path d="M9.5 9.5l5 5M14.5 9.5l-5 5" stroke="#fca5a5" strokeWidth="1.5" strokeLinecap={CAP} />
    )}
  </IconBase>
);

export const RefreshIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <path
      d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3"
      stroke={color}
      strokeWidth={S}
      strokeLinecap={CAP}
      strokeLinejoin={JOIN}
    />
    <path d="M19.5 5.5V9h-3.5" stroke={color} strokeWidth={S} strokeLinecap={CAP} strokeLinejoin={JOIN} />
  </IconBase>
);

export const PlayIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <path d="M9 7.5v9l7.5-4.5L9 7.5z" fill={color} />
  </IconBase>
);

export const StopIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <rect x="8" y="8" width="8" height="8" rx="1" fill={color} />
  </IconBase>
);

export const ServerIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <rect x="5" y="5" width="14" height="4.5" rx="1" stroke={color} strokeWidth="1.25" strokeOpacity="0.7" />
    <rect x="5" y="11.5" width="14" height="4.5" rx="1" stroke={color} strokeWidth="1.25" strokeOpacity="0.55" />
    <circle cx="16.5" cy="7.25" r="0.75" fill="#4ade80" />
    <circle cx="16.5" cy="13.75" r="0.75" fill="#4ade80" />
  </IconBase>
);

/* ── Scan module icons (animated when active) ── */

export const PathsModuleIcon: React.FC<ActiveIconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
  isActive = false,
}) => (
  <IconBase className={className} size={size}>
    <path
      d="M5 7.5h6l1.5 2H19v8.5H5V7.5z"
      stroke={color}
      strokeWidth={S}
      strokeLinejoin={JOIN}
      strokeOpacity="0.8"
    />
    <path d="M11 7.5V5.5h8v12.5" stroke={color} strokeWidth="1" strokeOpacity="0.35" strokeLinecap={CAP} />
    {isActive ? (
      <line x1="7" y1="13" x2="17" y2="13" stroke="#e53935" strokeWidth="1.25" strokeLinecap={CAP}>
        <animate attributeName="y1" values="11;15;11" dur="1.8s" repeatCount="indefinite" />
        <animate attributeName="y2" values="11;15;11" dur="1.8s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.4;1;0.4" dur="1.8s" repeatCount="indefinite" />
      </line>
    ) : (
      <path d="M7 13h10" stroke={color} strokeWidth="1" strokeOpacity="0.25" strokeLinecap={CAP} />
    )}
    <circle cx="17" cy="9" r="1" fill={isActive ? '#e53935' : color} fillOpacity={isActive ? 1 : 0.35} />
  </IconBase>
);

export const JsModuleIcon: React.FC<ActiveIconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
  isActive = false,
}) => (
  <IconBase className={className} size={size}>
    <path
      d="M8 6.5h8v11H8V6.5z"
      stroke={color}
      strokeWidth={S}
      strokeLinejoin={JOIN}
      strokeOpacity="0.75"
    />
    <path d="M10 10.5h1.5M14.5 10.5H16M10 14h6" stroke={color} strokeWidth="1" strokeLinecap={CAP} strokeOpacity="0.4" />
    <text x="10.5" y="13.5" fill={isActive ? '#e53935' : color} fontSize="5" fontWeight="700" fontFamily="monospace" opacity={isActive ? 1 : 0.5}>
      JS
    </text>
    {isActive && (
      <circle cx="17" cy="7" r="1.25" fill="#e53935">
        <animate attributeName="opacity" values="0.3;1;0.3" dur="1.5s" repeatCount="indefinite" />
        <animate attributeName="r" values="0.8;1.4;0.8" dur="1.5s" repeatCount="indefinite" />
      </circle>
    )}
  </IconBase>
);

export const GitModuleIcon: React.FC<ActiveIconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
  isActive = false,
}) => (
  <IconBase className={className} size={size}>
    <circle cx="8" cy="7" r="2" stroke={color} strokeWidth={S} strokeOpacity="0.8" />
    <circle cx="16" cy="7" r="2" stroke={color} strokeWidth={S} strokeOpacity="0.8" />
    <circle cx="12" cy="17" r="2" stroke={isActive ? '#e53935' : color} strokeWidth={S} />
    <path d="M8 9v3.5a4 4 0 0 0 4 4M16 9v3.5a4 4 0 0 1-4 4" stroke={color} strokeWidth="1.25" strokeLinecap={CAP} strokeOpacity="0.65" />
    {isActive && (
      <circle cx="12" cy="17" r="0.75" fill="#e53935">
        <animate attributeName="opacity" values="0.4;1;0.4" dur="2s" repeatCount="indefinite" />
      </circle>
    )}
  </IconBase>
);

export const RceModuleIcon: React.FC<ActiveIconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
  isActive = false,
}) => (
  <IconBase className={className} size={size}>
    <rect x="5" y="6" width="14" height="10" rx="1" stroke={color} strokeWidth={S} strokeOpacity="0.75" />
    <path d="M5 9h14" stroke={color} strokeWidth="1" strokeOpacity="0.35" />
    <circle cx="7" cy="7.5" r="0.5" fill="#ef4444" />
    <circle cx="8.75" cy="7.5" r="0.5" fill="#e53935" />
    <circle cx="10.5" cy="7.5" r="0.5" fill="#4ade80" />
    {isActive ? (
      <path d="M9 14.5l2-3.5 2 3.5" stroke="#e53935" strokeWidth="1.25" strokeLinecap={CAP} strokeLinejoin={JOIN}>
        <animate attributeName="opacity" values="0.4;1;0.4" dur="1.2s" repeatCount="indefinite" />
      </path>
    ) : (
      <path d="M9 13.5h6" stroke={color} strokeWidth="1" strokeOpacity="0.3" strokeLinecap={CAP} />
    )}
    {isActive && (
      <path d="M14 11l2-2M14 11l-1.5 1.5" stroke="#ef4444" strokeWidth="1.1" strokeLinecap={CAP}>
        <animate attributeName="opacity" values="0.3;1;0.3" dur="0.8s" repeatCount="indefinite" />
      </path>
    )}
  </IconBase>
);

export const MongoDbModuleIcon: React.FC<ActiveIconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
  isActive = false,
}) => (
  <IconBase className={className} size={size}>
    <path
      d="M12 4.5c-2.5 2-4 5.5-4 9a4 4 0 0 0 8 0c0-3.5-1.5-7-4-9z"
      stroke={isActive ? '#4ade80' : color}
      strokeWidth={S}
      strokeLinejoin={JOIN}
      fill={isActive ? 'rgba(74,222,128,0.12)' : 'none'}
      strokeOpacity="0.85"
    />
    <path d="M12 7v10" stroke={color} strokeWidth="1" strokeOpacity="0.35" strokeLinecap={CAP} />
    {isActive ? (
      <>
        <circle cx="9" cy="12" r="0.75" fill="#e53935">
          <animate attributeName="cy" values="11;13;11" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="15" cy="14" r="0.75" fill="#4ade80">
          <animate attributeName="cy" values="13;11;13" dur="2s" repeatCount="indefinite" />
        </circle>
      </>
    ) : (
      <>
        <circle cx="9" cy="12" r="0.6" fill={color} fillOpacity="0.3" />
        <circle cx="15" cy="14" r="0.6" fill={color} fillOpacity="0.3" />
      </>
    )}
  </IconBase>
);

export const BackupModuleIcon: React.FC<ActiveIconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
  isActive = false,
}) => (
  <IconBase className={className} size={size}>
    <ellipse cx="12" cy="8" rx="6" ry="2" stroke={color} strokeWidth={S} strokeOpacity="0.75" />
    <path d="M6 8v4c0 1.1 2.7 2 6 2s6-0.9 6-2V8" stroke={color} strokeWidth={S} strokeOpacity="0.65" />
    <path d="M6 12v3c0 1.1 2.7 2 6 2s6-0.9 6-2v-3" stroke={color} strokeWidth="1.25" strokeOpacity="0.45" />
    {isActive ? (
      <g>
        <path d="M12 15.5v3.5M10 17.5l2-2 2 2" stroke="#e53935" strokeWidth="1.25" strokeLinecap={CAP} strokeLinejoin={JOIN}>
          <animateTransform attributeName="transform" type="translate" values="0,0;0,-1.5;0,0" dur="1.6s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.45;1;0.45" dur="1.6s" repeatCount="indefinite" />
        </path>
      </g>
    ) : (
      <path d="M12 16v2.5M10.5 18l1.5-1.5 1.5 1.5" stroke={color} strokeWidth="1" strokeLinecap={CAP} strokeLinejoin={JOIN} strokeOpacity="0.3" />
    )}
  </IconBase>
);

export const WafBypassModuleIcon: React.FC<ActiveIconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
  isActive = false,
}) => (
  <IconBase className={className} size={size}>
    <path
      d="M12 4.5l6.5 2.8v4.2c0 3.2-2.5 5.9-6.5 6.8-4-0.9-6.5-3.6-6.5-6.8V7.3L12 4.5z"
      stroke={color}
      strokeWidth={S}
      strokeLinejoin={JOIN}
      strokeOpacity="0.75"
    />
    {isActive ? (
      <>
        <path d="M7 12h10" stroke="#ef4444" strokeWidth="1" strokeOpacity="0.35" strokeDasharray="2 2" />
        <path d="M8 12l8-4" stroke="#e53935" strokeWidth="1.25" strokeLinecap={CAP}>
          <animate attributeName="stroke-dashoffset" values="12;0" dur="1.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.4;1;0.4" dur="1.5s" repeatCount="indefinite" />
        </path>
        <circle cx="16" cy="8" r="1" fill="#e53935">
          <animate attributeName="cx" values="8;16;8" dur="2s" repeatCount="indefinite" />
          <animate attributeName="cy" values="12;8;12" dur="2s" repeatCount="indefinite" />
        </circle>
      </>
    ) : (
      <path d="M9 12h6" stroke={color} strokeWidth="1" strokeOpacity="0.3" strokeLinecap={CAP} />
    )}
  </IconBase>
);

/* ── Scan mode icons (animated when selected) ── */

export const DeepModeIcon: React.FC<ActiveIconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
  isActive = false,
}) => (
  <IconBase className={className} size={size}>
    <path
      d="M5 19.5h14M7 16h10M9 12.5h6"
      stroke={color}
      strokeWidth="1"
      strokeLinecap={CAP}
      strokeOpacity="0.35"
    />
    <ellipse cx="12" cy="9" rx="7" ry="2.5" stroke={color} strokeWidth={S} strokeOpacity="0.45" fill="none" />
    <ellipse cx="12" cy="9" rx="4.5" ry="1.6" stroke={color} strokeWidth="1.25" strokeOpacity="0.6" fill="none" />
    {isActive ? (
      <>
        <ellipse cx="12" cy="9" rx="2" ry="0.7" stroke="#e53935" strokeWidth="1.25" fill="none">
          <animate attributeName="rx" values="2;7.5;2" dur="2.4s" repeatCount="indefinite" />
          <animate attributeName="ry" values="0.7;2.8;0.7" dur="2.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.9;0;0.9" dur="2.4s" repeatCount="indefinite" />
        </ellipse>
        <line x1="12" y1="4.5" x2="12" y2="19" stroke="#e53935" strokeWidth="1.25" strokeLinecap={CAP}>
          <animate attributeName="opacity" values="0.25;1;0.25" dur="2s" repeatCount="indefinite" />
        </line>
        <circle cx="12" cy="9" r="1.5" fill="#e53935">
          <animate attributeName="r" values="1.2;2;1.2" dur="1.8s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.7;1;0.7" dur="1.8s" repeatCount="indefinite" />
        </circle>
      </>
    ) : (
      <circle cx="12" cy="9" r="1.25" fill={color} fillOpacity="0.35" />
    )}
    <path
      d="M12 6.5v-1.5M9.5 5.5l2.5 1 2.5-1"
      stroke={isActive ? '#c62828' : color}
      strokeWidth="1"
      strokeLinecap={CAP}
      strokeLinejoin={JOIN}
      strokeOpacity={isActive ? 0.85 : 0.4}
    />
  </IconBase>
);

export const MassScanModeIcon: React.FC<ActiveIconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
  isActive = false,
}) => (
  <IconBase className={className} size={size}>
    <path
      d="M4 8.5h11M4 12h14M4 15.5h9"
      stroke={color}
      strokeWidth={S}
      strokeLinecap={CAP}
      strokeOpacity="0.55"
    />
    <path
      d="M16.5 9.5l3.5 2.5-3.5 2.5V9.5z"
      stroke={isActive ? '#e53935' : color}
      strokeWidth={S}
      strokeLinejoin={JOIN}
      fill={isActive ? 'rgba(229, 57, 53,0.15)' : 'none'}
      strokeOpacity="0.85"
    />
    {isActive ? (
      <>
        <line x1="6" y1="8.5" x2="13" y2="8.5" stroke="#e53935" strokeWidth="1.25" strokeLinecap={CAP}>
          <animate attributeName="x1" values="4;10;4" dur="0.9s" repeatCount="indefinite" />
          <animate attributeName="x2" values="11;17;11" dur="0.9s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.3;1;0.3" dur="0.9s" repeatCount="indefinite" />
        </line>
        <line x1="6" y1="12" x2="15" y2="12" stroke="#c62828" strokeWidth="1.25" strokeLinecap={CAP}>
          <animate attributeName="x1" values="4;12;4" dur="0.7s" repeatCount="indefinite" />
          <animate attributeName="x2" values="13;20;13" dur="0.7s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.35;1;0.35" dur="0.7s" repeatCount="indefinite" />
        </line>
        <line x1="6" y1="15.5" x2="11" y2="15.5" stroke="#e53935" strokeWidth="1.25" strokeLinecap={CAP}>
          <animate attributeName="x1" values="4;8;4" dur="1.1s" repeatCount="indefinite" />
          <animate attributeName="x2" values="9;15;9" dur="1.1s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.3;1;0.3" dur="1.1s" repeatCount="indefinite" />
        </line>
        <circle cx="5" cy="12" r="1" fill="#4ade80">
          <animate attributeName="cx" values="5;19;5" dur="1.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0;1;0" dur="1.4s" repeatCount="indefinite" />
        </circle>
      </>
    ) : (
      <path d="M6 12h8" stroke={color} strokeWidth="1" strokeOpacity="0.2" strokeLinecap={CAP} strokeDasharray="2 3" />
    )}
  </IconBase>
);

const ADDON_ICON_MAP: Record<string, React.FC<IconProps>> = {
  grabber: GrabberIcon,
  mailer: MailerIcon,
  debouncer: DebouncerIcon,
  stripe: StripeIcon,
  premiumproxy: ConfigIcon,
};

export const HitsIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <circle cx="12" cy="12" r="8" stroke={color} strokeWidth={S} strokeOpacity="0.5" />
    <circle cx="12" cy="12" r="4" stroke={color} strokeWidth={S} strokeOpacity="0.75" />
    <circle cx="12" cy="12" r="1.5" fill="#E8132E" />
    <path d="M12 4v2M12 18v2M4 12h2M18 12h2" stroke={color} strokeWidth="1" strokeLinecap={CAP} strokeOpacity="0.35" />
  </IconBase>
);

export const AdminIcon: React.FC<IconProps> = ({
  className = '',
  size = 24,
  color = 'currentColor',
}) => (
  <IconBase className={className} size={size}>
    <path
      d="M12 3.5l6.5 2.8v4.2c0 3.2-2.5 5.9-6.5 6.8-4-0.9-6.5-3.6-6.5-6.8V6.3L12 3.5z"
      stroke={color}
      strokeWidth={S}
      strokeLinejoin={JOIN}
      strokeOpacity="0.8"
    />
    <path d="M9.5 10.5l1.8 1.8L15 8.5" stroke="#E8132E" strokeWidth="1.25" strokeLinecap={CAP} strokeLinejoin={JOIN} />
  </IconBase>
);

export const AddonModuleIcon: React.FC<IconProps & { addonKey: string }> = ({
  addonKey,
  size = 28,
  className = '',
}) => {
  const key = addonKey.toLowerCase().replace(/\s+/g, '');
  const Icon = ADDON_ICON_MAP[key] ?? InjectedMarkIcon;
  return <Icon size={size} className={className} />;
};
